/**
 * Phase 114.A.2 — "Add to Gradebook" button for the Lesson Workspace.
 *
 * Surfaces only for Pro users whose Gradebook is set up AND whose
 * Gradebook contains the unit this lesson belongs to. For everyone
 * else the button is hidden — no upsell prompts inside the lesson view
 * (same treatment as our other tier-gated affordances).
 *
 * Click → popover lists the lesson's parsed success criteria. Picking
 * one navigates to /gradebook with prefill params; the Gradebook
 * resolves the matching unit and opens the Add Assessment form
 * pre-populated with: criterion text, linked expectation codes,
 * today's date, type=formative (editable), mark_type=completion
 * (editable). NOTHING is recorded until the teacher confirms.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, ChevronDown, Layers } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { getGradebookUnits } from "../lib/api";

const SUCCESS_HEADING_RE = /^#+\s*Success Criteria\s*$/im;

/** Parse the lesson markdown for the "Success Criteria" section and
 *  return its bullet items (each "- " line) as plain text. */
function parseSuccessCriteria(markdown) {
  if (!markdown) return [];
  const m = markdown.split(/\r?\n/);
  let inSection = false;
  const items = [];
  for (const raw of m) {
    if (SUCCESS_HEADING_RE.test(raw)) {
      inSection = true;
      continue;
    }
    if (inSection) {
      // Stop at the next heading.
      if (/^#+\s/.test(raw)) break;
      const trimmed = raw.trim();
      if (!trimmed) continue;
      // Accept "- ", "* ", or "1. " style bullets.
      const bullet = trimmed.match(/^(?:[-*]|\d+\.)\s+(.*)$/);
      if (bullet) items.push(bullet[1].trim());
    }
  }
  return items;
}

const AddToGradebookButton = ({ activity, isOwner, isPaid }) => {
  const navigate = useNavigate();
  const [gradebookUnits, setGradebookUnits] = useState(null); // null = unknown, [] = none
  const [open, setOpen] = useState(false);

  // The "unit activity id" we should match against the Gradebook's
  // units feed. For a lesson, that's `parent_unit_id`; for a unit
  // page, it's the activity's own id.
  const unitActivityId =
    activity?.output_type === "unit"
      ? activity.id
      : activity?.parent_unit_id || null;

  useEffect(() => {
    // Only Pro owners get the Gradebook surface — every other case is
    // hidden silently, no upsell.
    if (!isOwner || !isPaid || !unitActivityId) {
      setGradebookUnits([]);
      return;
    }
    let cancelled = false;
    getGradebookUnits()
      .then((res) => {
        if (cancelled) return;
        setGradebookUnits(Array.isArray(res?.units) ? res.units : []);
      })
      .catch(() => {
        if (cancelled) return;
        // 402/403 (non-Pro) or transient → hide.
        setGradebookUnits([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOwner, isPaid, unitActivityId]);

  const matchingGradebookUnit = useMemo(() => {
    if (!gradebookUnits || !unitActivityId) return null;
    return gradebookUnits.find((u) => u.unit_activity_id === unitActivityId);
  }, [gradebookUnits, unitActivityId]);

  const criteria = useMemo(() => {
    const fromArray = Array.isArray(activity?.success_criteria)
      ? activity.success_criteria.filter(Boolean)
      : [];
    if (fromArray.length > 0) return fromArray;
    return parseSuccessCriteria(
      activity?.lesson_markdown || activity?.unit_markdown || ""
    );
  }, [activity]);

  // Hidden when:
  //  - not Pro owner
  //  - no matching Gradebook unit (Gradebook either not set up, or
  //    teacher hasn't added this unit to the LRP yet)
  //  - no parsed success criteria to surface
  if (!isOwner || !isPaid) return null;
  if (gradebookUnits === null) return null; // still loading; show nothing
  if (!matchingGradebookUnit) return null;
  if (criteria.length === 0) return null;

  const codes = Array.from(
    new Set(
      [
        ...(activity?.expectations || []),
        ...(activity?.curriculum_links || []),
      ]
        .map((c) => (typeof c === "string" ? c : c?.code || ""))
        .map((c) => (c || "").toUpperCase().trim())
        .filter(Boolean)
    )
  );
  const today = new Date().toISOString().slice(0, 10);

  const handlePick = (criterion) => {
    const params = new URLSearchParams({
      prefill_unit_activity_id: unitActivityId,
      prefill_criterion: criterion,
      prefill_codes: codes.join(","),
      prefill_date: today,
    });
    setOpen(false);
    navigate(`/gradebook?${params.toString()}`);
  };

  const handleBundleAll = () => {
    // "Use all criteria" — bundle every parsed success criterion into
    // one summative column. Per spec this is summative + level scale by
    // default (rubric-shaped); the teacher can still override on the
    // form before confirming, and a confirmation summary lists every
    // bundled criterion since the blast radius is larger than the
    // single-criterion path.
    const params = new URLSearchParams({
      prefill_unit_activity_id: unitActivityId,
      prefill_all_criteria: "1",
      prefill_criteria_list: JSON.stringify(criteria),
      prefill_codes: codes.join(","),
      prefill_date: today,
    });
    setOpen(false);
    navigate(`/gradebook?${params.toString()}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="lesson-add-to-gradebook"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#4a6984]/15 text-[#1c2d3d] text-xs font-extrabold hover:bg-[#f4f5f7]"
        >
          <ClipboardCheck className="h-3.5 w-3.5" />
          Add to Gradebook
          <ChevronDown className="h-3.5 w-3.5 -mr-0.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] bg-white p-3"
        data-testid="lesson-add-to-gradebook-popover"
      >
        <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/80 mb-1">
          Pick a success criterion
        </p>
        <p className="text-[11px] text-[#4a6984] leading-snug mb-2.5">
          Pre-fills the Add Assessment form in your Gradebook. You set the type
          and confirm — nothing records until then.
        </p>

        {/* "Use all criteria" bundle — culminating task path */}
        {criteria.length >= 2 ? (
          <button
            type="button"
            onClick={handleBundleAll}
            data-testid="lesson-add-to-gradebook-bundle"
            className="w-full text-left rounded-md border border-[#1c2d3d] bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 transition-colors px-3 py-2.5 text-[12px] text-white leading-snug mb-2 flex items-start gap-2"
          >
            <Layers className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              <span className="block font-extrabold tracking-tight">
                Use all {criteria.length} criteria
              </span>
              <span className="block text-[10.5px] text-white/80 mt-0.5 leading-snug">
                One column for the full task. Mark each criterion on the rubric panel.
              </span>
            </span>
          </button>
        ) : null}

        <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/55 mt-2 mb-1.5">
          {criteria.length >= 2 ? "Or pick just one" : "Pick one"}
        </p>
        <ul className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
          {criteria.map((c, idx) => (
            <li key={idx}>
              <button
                type="button"
                onClick={() => handlePick(c)}
                data-testid={`lesson-add-to-gradebook-pick-${idx}`}
                className="w-full text-left rounded-md border border-[#96ded1] bg-[#96ded1]/12 hover:bg-[#96ded1]/25 transition-colors px-3 py-2 text-[12px] text-[#1c2d3d] leading-snug"
              >
                {c}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default AddToGradebookButton;
